import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';

const Section = styled.section`
  padding: 6rem 6%;
  background-color: #fafafa;
  
  @media (max-width: 768px) {
    padding: 4rem 6%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-align: center;
  color: #000;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FaqContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const FaqItem = styled.div`
  margin-bottom: 1rem;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FaqHeader = styled.div`
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const FaqQuestion = styled.h4`
  font-size: 1.1rem;
  color: #000;
  margin: 0;
  font-weight: 600;
`;

const IconContainer = styled.div`
  transform: ${({ isOpen }) => isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FaqContent = styled(motion.div)`
  padding: 0 1.5rem;
  overflow: hidden;
`;

const FaqAnswer = styled.div`
  color: #666;
  line-height: 1.7;
  padding-bottom: 1.5rem;
`;

const FaqItemComponent = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <FaqItem>
      <FaqHeader onClick={() => setIsOpen(!isOpen)}>
        <FaqQuestion>{question}</FaqQuestion>
        <IconContainer isOpen={isOpen}>
          <FaChevronDown />
        </IconContainer>
      </FaqHeader>
      <AnimatePresence>
        {isOpen && (
          <FaqContent
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <FaqAnswer>{answer}</FaqAnswer>
          </FaqContent>
        )}
      </AnimatePresence>
    </FaqItem>
  );
};

const Faq = () => {
  const faqItems = [
    {
      question: "What types of startups do you work with?",
      answer: "We work with startups across various industries, with a particular focus on tech, ecommerce, and digital services. From pre-seed to Series A and beyond, we tailor our services to match your growth stage."
    },
    {
      question: "How do your pricing models work?",
      answer: "We offer flexible pricing options including fixed fee packages, monthly retainers, and project-based pricing. Our goal is to provide predictable costs that align with your startup's budget and needs."
    },
    {
      question: "Can you help with international expansion?",
      answer: "Yes, we specialize in helping startups navigate global markets. Our expertise covers cross-border contracts, international compliance requirements, and structuring your business for efficient global operations."
    },
    {
      question: "What happens during the free consultation?",
      answer: "During our 30-60 minute consultation, we'll discuss your startup's legal needs, identify potential challenges, and outline how our services could support your growth. It's a no-obligation opportunity to see if we're a good fit for your business."
    }
  ];

  return (
    <Section id="faq">
      <SectionTitle>Frequently Asked Questions</SectionTitle>
      <FaqContainer>
        {faqItems.map((item, index) => (
          <motion.div 
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <FaqItemComponent 
              question={item.question}
              answer={item.answer}
            />
          </motion.div>
        ))}
      </FaqContainer>
    </Section>
  );
};

export default Faq; 